import { useState, useEffect, lazy, Suspense } from 'react';
import { Heart, MapPin, TrendingDown, Zap, Search, X, Crown } from 'lucide-react';
import { SignedIn, SignedOut, SignInButton, SignUpButton, UserButton, useAuth } from '@clerk/clerk-react';
import { SearchBar } from './components/SearchBar';
import { FilterPanel, FilterState } from './components/FilterPanel';
import { ProductCard } from './components/ProductCard';
import { AdCard } from './components/AdCard';
import { PriceTracker } from './components/PriceTracker';
import { ComparisonView } from './components/ComparisonView';
// Lazy load LocationMap (includes heavy leaflet library) - only load when map tab is used
const LocationMap = lazy(() => import('./components/LocationMap').then(module => ({ default: module.LocationMap })));
import { ProductDetailsModal } from './components/ProductDetailsModal';
import { SubscriptionModal } from './components/SubscriptionModal';
import { SubscriptionManagement } from './components/SubscriptionManagement';
import { useProductSearch } from './hooks/useProductSearch';
import { useFavorites } from './hooks/useFavorites';
import { usePriceTracking } from './hooks/usePriceTracking';
import { useSubscription } from './hooks/useSubscription';
import { useCraigslistImageLoader } from './hooks/useCraigslistImageLoader';
import { ProductListing, LocationResult, ProductDetails } from './types';
import { getProductDetails } from './services/api';

type TabType = 'search' | 'favorites' | 'tracking' | 'map';

function App() {
  const { userId } = useAuth();
  // Note: userEmail is obtained from useSubscription hook
  
  const [activeTab, setActiveTab] = useState<TabType>('search');
  const [comparisonProducts, setComparisonProducts] = useState<ProductListing[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ sources: [] });
  const [recentSearches, setRecentSearches] = useState<{ query: string; location: string | null }[]>([]);
  const [selectedProductDetails, setSelectedProductDetails] = useState<ProductDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);

  const { listings, loading, loadingMore, error, search, loadMore, hasMore, locations, loadingLocations, searchLocations } = useProductSearch();
  const { favorites, toggleFavorite } = useFavorites();
  const { trackingList, addTracking, removeTracking, priceHistory, getTrackedProduct } = usePriceTracking();
  const { isPremium, tier, upgrade, loading: subscriptionLoading, adFree, maxPriceAlerts, maxSavedSearches } = useSubscription();
  
  // Automatically fetch images for Craigslist listings with default images
  const { listings: listingsWithImages, isImageLoading } = useCraigslistImageLoader(listings);
  
  // Free tier users now get all results - limitation is on number of searches, not results
  const displayedListings = listingsWithImages;

  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      setRecentSearches(JSON.parse(stored));
    }
  }, []);

  const handleSearch = (query: string, location?: LocationResult | string) => {
    // Update URL with search query parameter
    const url = new URL(window.location.href);
    if (query.trim()) {
      url.searchParams.set('q', query.trim());
      // Optionally add location to URL if it's a string
      if (location && typeof location === 'string') {
        url.searchParams.set('location', location);
      } else if (location && typeof location === 'object') {
        url.searchParams.set('location', location.name);
      } else {
        url.searchParams.delete('location');
      }
    } else {
      // Clear search params if query is empty
      url.searchParams.delete('q');
      url.searchParams.delete('location');
    }
    
    // Update URL without page reload (using replaceState to avoid adding to history)
    window.history.replaceState({}, '', url.toString());

    // Pass location directly to search (can be LocationResult with coordinates or string)
    // Include query filtering options from filters
    search(query, location, {
      limit: 100, // Increased default limit
      ...filters,
      enableQueryFilter: filters.enableQueryFilter ?? true, // Default to true
      minRelevanceScore: filters.minRelevanceScore ?? 0.3, // Default to 0.3
    });

    // Convert location to string for storage
    const locationString = typeof location === 'object' ? location.name : location;
    const newSearch = { query, location: locationString || null };
    const updated = [newSearch, ...recentSearches.filter((s) => !(s.query === query && s.location === locationString))].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Read search query from URL on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryParam = urlParams.get('q');
    if (queryParam && queryParam.trim()) {
      // If there's a query in the URL, perform the search
      const locationParam = urlParams.get('location');
      const locationToUse = locationParam || undefined;
      handleSearch(queryParam.trim(), locationToUse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - handleSearch is stable enough

  const handleAddToComparison = (product: ProductListing) => {
    if (!comparisonProducts.find((p) => p.id === product.id)) {
      setComparisonProducts((prev) => [...prev, product]);
    }
  };

  const handleRemoveFromComparison = (productId: string) => {
    setComparisonProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleToggleTracking = async (product: ProductListing) => {
    const tracked = getTrackedProduct(product.id);
    if (tracked) {
      removeTracking(tracked.id);
    } else {
      // Check subscription limit before adding
      if (trackingList.length >= maxPriceAlerts) {
        alert(`You've reached your price alert limit (${maxPriceAlerts}). Upgrade to ${maxPriceAlerts === 5 ? 'Pro' : maxPriceAlerts === 20 ? 'Business' : 'Enterprise'} for more alerts.`);
        setShowSubscriptionModal(true);
        return;
      }
      addTracking(product);
    }
  };

  const handleToggleFavorite = async (product: ProductListing) => {
    const isFav = favorites.some((f) => f.product_id === product.id);
    if (!isFav) {
      // Check subscription limit before adding
      if (favorites.length >= maxSavedSearches) {
        alert(`You've reached your saved searches limit (${maxSavedSearches}). Upgrade to ${maxSavedSearches === 10 ? 'Pro' : maxSavedSearches === 50 ? 'Business' : 'Enterprise'} for more saved searches.`);
        setShowSubscriptionModal(true);
        return;
      }
    }
    toggleFavorite(product);
  };

  const handleViewDetails = async (product: ProductListing) => {
    setLoadingDetails(true);
    setDetailsError(null);
    try {
      // For Craigslist, use the URL instead of product_id since the API needs the full URL
      const productId = product.marketplace === 'Craigslist' && product.url 
        ? product.url 
        : product.id;
      const details = await getProductDetails(productId, product.marketplace, undefined, userId || undefined, undefined);
      setSelectedProductDetails(details);
    } catch (err) {
      setDetailsError(err instanceof Error ? err.message : 'Failed to load product details');
      console.error('Error loading product details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleUpgrade = async (tier: 'pro' | 'business' | 'enterprise') => {
    try {
      await upgrade(tier);
    } catch (err) {
      console.error('Upgrade error:', err);
    }
  };

  const handleCloseDetails = () => {
    setSelectedProductDetails(null);
    setDetailsError(null);
  };

  const stats = [
    { label: 'Favorites', value: favorites.length, icon: Heart, color: 'text-red-500 dark:text-red-400' },
    { label: 'Tracking', value: trackingList.length, icon: TrendingDown, color: 'text-orange-500 dark:text-orange-400' },
    { label: 'Comparing', value: comparisonProducts.length, icon: Zap, color: 'text-accent-600 dark:text-accent-400' },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-accent-600 focus:text-white focus:rounded-lg focus:shadow-lg"
        aria-label="Skip to main content"
      >
        Skip to main content
      </a>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-800" role="banner">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {/* Logo - Critical for LCP, but needs optimization (currently 1.3MB) */}
              <img 
                src="/logo.png" 
                alt="Dryv - Find Used Cars for Sale" 
                className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-xl shadow-sm object-contain"
                width="48"
                height="48"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                  <span className="sr-only">Dryv - </span>Marketplace Search
                </h1>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 hidden sm:block">Compare prices across platforms</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
              <SignedIn>
                <button
                  onClick={() => setShowSubscriptionManagement(true)}
                  className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 text-xs sm:text-sm font-medium shadow-sm ${
                    isPremium
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800'
                      : 'bg-gradient-to-r from-accent-600 to-accent-700 text-white hover:from-accent-700 hover:to-accent-800'
                  }`}
                  aria-label={isPremium ? 'Manage subscription' : 'Upgrade subscription'}
                >
                  <Crown size={14} className="sm:w-4 sm:h-4" aria-hidden="true" />
                  <span className="hidden md:inline">{isPremium ? 'Manage' : 'Upgrade'}</span>
                </button>
              </SignedIn>
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={stat.label}
                    className="hidden xs:flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-neutral-100/50 dark:bg-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors duration-200"
                  >
                    <Icon size={16} className={`${stat.color} sm:w-[18px] sm:h-[18px]`} />
                    <div className="flex flex-col">
                      <div className="text-xs sm:text-sm font-semibold text-neutral-900 dark:text-neutral-100 leading-none">{stat.value}</div>
                      <div className="text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 leading-none mt-0.5">{stat.label}</div>
                    </div>
                  </div>
                );
              })}
              <SignedOut>
                <div className="flex items-center gap-2">
                  <SignInButton mode="modal">
                    <button className="btn px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors duration-200">
                      Sign In
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button className="btn px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm bg-accent-600 text-white hover:bg-accent-700 transition-colors duration-200">
                      Sign Up
                    </button>
                  </SignUpButton>
                </div>
              </SignedOut>
              <SignedIn>
                <UserButton />
              </SignedIn>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-12" role="main" aria-label="Main content">
        {/* Search Section */}
        <section aria-label="Search for used cars" className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
          <div className="card card-hover p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
            <SearchBar
              onSearch={handleSearch}
              onLocationSearch={searchLocations}
              suggestedLocations={locations}
              loadingLocations={loadingLocations}
              loading={loading}
              recentSearches={recentSearches}
            />
          </div>
          
          {/* SEO-friendly heading for used cars */}
          <h2 className="sr-only">Search Used Cars for Sale</h2>

          {/* Tab Navigation */}
          <nav aria-label="Main navigation" className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              onClick={() => setActiveTab('search')}
              className={`btn px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 ${
                activeTab === 'search'
                  ? 'bg-accent-600 text-white shadow-md'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700'
              }`}
              aria-label="Search results"
              aria-current={activeTab === 'search' ? 'page' : undefined}
            >
              <Search size={14} className="sm:w-4 sm:h-4 md:hidden" aria-hidden="true" />
              <span className="hidden md:inline">Search Results</span>
              <span className="md:hidden">Search</span>
              {listingsWithImages.length > 0 && (
                <span className={`ml-1 sm:ml-1.5 px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold ${
                  activeTab === 'search' ? 'bg-white/20' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
                }`}>
                  {listingsWithImages.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('favorites')}
              className={`btn px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 ${
                activeTab === 'favorites'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700'
              }`}
              aria-label={`Favorites${favorites.length > 0 ? ` (${favorites.length} items)` : ''}`}
              aria-current={activeTab === 'favorites' ? 'page' : undefined}
            >
              <Heart size={14} className={`sm:w-4 sm:h-4 ${activeTab === 'favorites' ? 'fill-current' : ''}`} aria-hidden="true" />
              <span className="hidden sm:inline">Favorites</span>
              {favorites.length > 0 && (
                <span className={`ml-1 sm:ml-1.5 px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold ${
                  activeTab === 'favorites' ? 'bg-white/20' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
                }`}>
                  {favorites.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`btn px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 ${
                activeTab === 'tracking'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700'
              }`}
              aria-label={`Price tracking${trackingList.length > 0 ? ` (${trackingList.length} items)` : ''}`}
              aria-current={activeTab === 'tracking' ? 'page' : undefined}
            >
              <TrendingDown size={14} className="sm:w-4 sm:h-4" aria-hidden="true" />
              <span className="hidden md:inline">Price Tracking</span>
              <span className="md:hidden">Tracking</span>
              {trackingList.length > 0 && (
                <span className={`ml-1 sm:ml-1.5 px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold ${
                  activeTab === 'tracking' ? 'bg-white/20' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
                }`}>
                  {trackingList.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('map')}
              className={`btn px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 ${
                activeTab === 'map'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700'
              }`}
              aria-label="Map view"
              aria-current={activeTab === 'map' ? 'page' : undefined}
            >
              <MapPin size={14} className="sm:w-4 sm:h-4" aria-hidden="true" />
              <span className="hidden sm:inline">Map</span>
            </button>
          </nav>
        </section>

        {/* Map Tab Content */}
        {activeTab === 'map' && (
          <div className="mb-6 sm:mb-8 animate-fade-in">
            {listingsWithImages.length > 0 ? (
              <Suspense fallback={
                <div className="card p-12 sm:p-16 text-center">
                  <div className="max-w-md mx-auto">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto mb-4"></div>
                    <p className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400">Loading map...</p>
                  </div>
                </div>
              }>
                <LocationMap products={listingsWithImages} onClose={() => setActiveTab('search')} />
              </Suspense>
            ) : (
              <div className="card p-12 sm:p-16 text-center">
                <div className="max-w-md mx-auto">
                  <MapPin size={40} className="sm:w-12 sm:h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" aria-hidden="true" />
                  <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    No results to show on the map
                  </h3>
                  <p className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400">
                    Run a search with location data to view listings on the map.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search Tab Content */}
        {activeTab === 'search' && (
          <section aria-label="Used car search results" className="space-y-4 sm:space-y-6">
            <div className="w-full sm:max-w-xs">
              <FilterPanel isOpen={isFilterOpen} onToggle={() => setIsFilterOpen(!isFilterOpen)} onFilterChange={setFilters} />
            </div>

            {error && (
              <div className="card p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 animate-fade-in">
                <p className="text-xs sm:text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
              </div>
            )}

            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="card h-80 sm:h-96 animate-pulse">
                    <div className="bg-neutral-200 dark:bg-neutral-700 h-48 sm:h-56 rounded-t-xl"></div>
                    <div className="p-4 sm:p-5 space-y-3">
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4"></div>
                      <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2"></div>
                      <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {listings.length === 0 && !loading && (
              <div className="card p-12 sm:p-16 text-center animate-fade-in">
                <div className="max-w-md mx-auto">
                  <Search size={40} className="sm:w-12 sm:h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Start Your Used Car Search</h3>
                  <p className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400">Search thousands of used cars for sale across Facebook Marketplace, OfferUp, Craigslist, and eBay</p>
                </div>
              </div>
            )}


            {displayedListings.length > 0 && !loading && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 animate-fade-in">
                  {displayedListings
                    .filter((product) => {
                      // Hide ads for premium users with ad_free subscription
                      if (product.is_ad && adFree) {
                        return false;
                      }
                      return true;
                    })
                    .map((product) => {
                    // Render AdCard for ads, ProductCard for regular products
                    if (product.is_ad) {
                      return <AdCard key={product.id} product={product} />;
                    }

                    const fav = favorites.find((f) => f.product_id === product.id);
                    const tracked = getTrackedProduct(product.id);

                    return (
                      <ProductCard
                        key={product.id}
                        product={product}
                        isFavorite={!!fav}
                        onToggleFavorite={handleToggleFavorite}
                        onAddToComparison={handleAddToComparison}
                        isTracked={!!tracked}
                        onToggleTracking={handleToggleTracking}
                        trackedProduct={tracked}
                        onViewDetails={handleViewDetails}
                        isImageLoading={isImageLoading(product.id)}
                      />
                    );
                  })}
                </div>
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center mt-6 sm:mt-8">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="btn-primary px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 sm:gap-3"
                    >
                      {loadingMore ? (
                        <>
                          <div className="inline-block animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                          <span>Loading more...</span>
                        </>
                      ) : (
                        <>
                          <span>Load More Results</span>
                          <span className="text-xs sm:text-sm opacity-75">({displayedListings.length} shown)</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Favorites Tab Content */}
        {activeTab === 'favorites' && (
          <div className="space-y-4 sm:space-y-6 animate-fade-in">
            {favorites.length === 0 ? (
              <div className="card p-12 sm:p-16 text-center">
                <Heart size={40} className="sm:w-12 sm:h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">No favorites yet</h3>
                <p className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400">Save products to compare later</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {favorites.map((favorite) => {
                  const fullProduct: ProductListing = {
                    id: favorite.product_id,
                    title: favorite.title,
                    price: favorite.price,
                    images: favorite.image_url ? [favorite.image_url] : [],
                    location: null,
                    marketplace: (favorite.marketplace as 'FBM' | 'OfferUp' | 'Amazon' | 'eBay' | 'Craigslist') || 'FBM',
                    url: favorite.url,
                  };

                  const tracked = getTrackedProduct(favorite.product_id);

                  return (
                    <ProductCard
                      key={favorite.id}
                      product={fullProduct}
                      isFavorite={true}
                      onToggleFavorite={toggleFavorite}
                      onAddToComparison={handleAddToComparison}
                      isTracked={!!tracked}
                      onToggleTracking={handleToggleTracking}
                      trackedProduct={tracked}
                      onViewDetails={handleViewDetails}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tracking Tab Content */}
        {activeTab === 'tracking' && (
          <div className="animate-fade-in">
            <PriceTracker
              trackingList={trackingList}
              priceHistory={priceHistory}
              onRemoveTracking={removeTracking}
            />
          </div>
        )}
      </main>

      {/* Footer with SEO content */}
      <footer className="bg-neutral-100 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 mt-12" role="contentinfo">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3 sm:mb-4">Find Used Cars for Sale</h3>
              <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Search thousands of used cars for sale across Facebook Marketplace, OfferUp, Craigslist, and eBay. Compare prices, track price drops, and find the best deals on pre-owned vehicles near you.
              </p>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3 sm:mb-4">Why Choose Dryv?</h3>
              <ul className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 space-y-2">
                <li>• Search multiple marketplaces at once</li>
                <li>• Compare used car prices across platforms</li>
                <li>• Track price drops on your favorite listings</li>
                <li>• Find used cars near your location</li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3 sm:mb-4">Popular Used Car Searches</h3>
              <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Search for used Toyota Camry, Honda Accord, Ford F-150, Chevrolet Silverado, and more. Find affordable used cars, luxury pre-owned vehicles, and everything in between.
              </p>
            </div>
          </div>
          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-neutral-200 dark:border-neutral-700 text-center">
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">
              © {new Date().getFullYear()} Dryv. Search used cars for sale across multiple marketplaces.
            </p>
          </div>
        </div>
      </footer>

      {/* Comparison Modal */}
      {showComparison && comparisonProducts.length > 0 && (
        <ComparisonView
          products={comparisonProducts}
          onRemoveProduct={handleRemoveFromComparison}
          onClose={() => setShowComparison(false)}
        />
      )}

      {/* Comparison FAB */}
      {comparisonProducts.length > 0 && !showComparison && (
        <button
          onClick={() => setShowComparison(true)}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 btn-primary rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl transition-all duration-200 z-40 animate-scale-in"
          aria-label="Open comparison"
        >
          <Zap size={18} className="sm:w-5 sm:h-5" />
          <span className="hidden sm:inline ml-2 font-semibold">Compare</span>
          <span className="ml-1 sm:ml-1.5 px-1.5 sm:px-2 py-0.5 rounded-full bg-white/20 text-[10px] sm:text-xs font-bold">
            {comparisonProducts.length}
          </span>
        </button>
      )}

      {/* Product Details Modal */}
      {selectedProductDetails && (
        <ProductDetailsModal
          product={selectedProductDetails}
          onClose={handleCloseDetails}
        />
      )}

      {/* Loading Details Overlay */}
      {loadingDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-2xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto mb-4"></div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">Loading product details...</p>
            </div>
          </div>
        </div>
      )}

      {/* Details Error Toast */}
      {detailsError && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-fade-in">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">Failed to load details</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{detailsError}</p>
              </div>
              <button
                onClick={() => setDetailsError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentTier={tier}
        onUpgrade={async (tier) => {
          if (tier !== 'free') {
            await handleUpgrade(tier);
          }
        }}
        loading={subscriptionLoading}
      />

      {/* Subscription Management */}
      <SubscriptionManagement
        isOpen={showSubscriptionManagement}
        onClose={() => setShowSubscriptionManagement(false)}
      />
    </div>
  );
}

export default App;
